import { Box } from '@gemini/core';
import { CheckFilledCircleIcon, ExclamationFilledCircleIcon, RotateIcon } from '@gemini/icons';
import { Button } from '@gemini/ui';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { EActionType } from '@mfe-types/context';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import useSmsService from '@mfe-presentation/hooks/useSmsService';
import { useFormContext } from 'react-hook-form';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { extractPhoneNumber } from '@aviv-estimation-funnel/shells/src/app/helpers/extractPhoneNumber';
import { addError } from '@mfe-helpers/datadog';
import { ResultStatus, SmsServiceCode } from '@mfe-presentation/constants/userVerification';

const PhoneNumberRequestNewCode = () => {
    const { t } = useTranslation(NAME_SPACE);
    const dispatch = useAppDispatch();
    const { setValue } = useFormContext();
    const {
        config,
        currentStep: { name },
        formValues,
        timer: { seconds, startTimer },
        userVerification,
    } = useAppContext();

    const requestNewCode = async () => {
        setValue('phoneNumberVerificationCode', '');
        try {
            dispatch({
                type: EActionType.SetUserVerification,
                payload: { ...userVerification, isFetching: true },
            });
            const { phoneNumber: rawPhoneNumber } = userVerification;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const response = await useSmsService(
                {
                    ...formValues,
                    ...(rawPhoneNumber && {
                        phoneNumber: extractPhoneNumber(rawPhoneNumber).phoneNumber,
                        phoneNumberRegionCode:
                            extractPhoneNumber(rawPhoneNumber).phoneNumberRegionCode,
                    }),
                },
                config,
                name,
            );

            dispatch({
                type: EActionType.SetUserVerification,
                payload: { ...response, isFetching: false },
            });

            if (response?.isVerificationRequired) {
                startTimer();

                dispatch({
                    type: EActionType.SetToastMessage,
                    payload: {
                        type: 'success',
                        status:
                            response.status === ResultStatus.SmsSent
                                ? SmsServiceCode.VerificationCodeSent
                                : response.status,
                        params: {
                            number: rawPhoneNumber,
                        },
                    },
                });
            }
        } catch (error: any) {
            addError(error);
            dispatch({
                type: EActionType.SetToastMessage,
                payload: {
                    status: SmsServiceCode.VerificationCodeServerError,
                },
            });
            dispatch({
                type: EActionType.SetUserVerification,
                payload: {
                    ...userVerification,
                    isFetching: false,
                },
            });
        }
    };

    const displayRequestNewCode = seconds === 0;

    const countdown = `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

    return displayRequestNewCode ? (
        <>
            <Box
                as="p"
                typography="typography.body.14.regular"
                testId="phone-number-verification-additional-text"
                marginVertical="spacing.4"
            >
                <ExclamationFilledCircleIcon
                    color="color.content.status.warning.default"
                    size="sizing.20"
                />
                <Box as="span" marginLeft="spacing.8">
                    {t(`step_${name}.additionalText`)}
                </Box>
            </Box>
            <Button
                marginTop="spacing.16"
                onPress={requestNewCode}
                isDisabled={userVerification.isFetching}
                icon={RotateIcon}
                // @ts-expect-error width is not in the props
                width="100%"
            >
                {t(`step_${name}.requestNewCode`)}
            </Button>
        </>
    ) : (
        <Box
            as="p"
            typography="typography.body.14.regular"
            marginVertical="spacing.4"
            display="flex"
            alignItems="center"
        >
            <CheckFilledCircleIcon color="color.content.status.success.default" size="sizing.20" />
            <Box as="span" marginLeft="spacing.8">
                <div
                    //  eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: t(`step_${name}.countdownValidity`, {
                            replace: { time: countdown },
                        }),
                    }}
                />
            </Box>
        </Box>
    );
};

export default PhoneNumberRequestNewCode;
